import { Component, computed, input, output, signal, viewChild, ElementRef, effect } from '@angular/core';
import { Student } from '../../../core/models/student.model';
import { BatchPrintOptions, BatchPrintTypes, DiagnosticTestType } from '../../../core/models/print-opts';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { FormsModule } from '@angular/forms';
import { NgForOf, NgIf } from '@angular/common';
import { faLayerGroup, faSpinner } from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'wf-batch-print-dialog',
  standalone: true,
  imports: [
    FontAwesomeModule,
    FormsModule,
    NgIf
  ],
  templateUrl: './batch-print-dialog.component.html',
  styleUrls: ['../../../core/styles/dialog.css', './batch-print-dialog.component.css'],
})
export class BatchPrintDialogComponent {
  batchPrintIcon = faLayerGroup ;
  loadingIcon = faSpinner ;
  dialogElement = viewChild<ElementRef<HTMLDialogElement>>('bulkPrintDialog');
  printAll = signal<boolean>(true);
  searchTerm = signal<string>('');
  selectedTestTypes = signal<DiagnosticTestType[]>([]);
  isOpen = input<boolean>(false);
  printType = input<BatchPrintTypes>();
  students = input<Student[]>([]);
  isGenerating = input<boolean>(false);
  onClose = output<void>();
  onPrint = output<BatchPrintOptions>();

  readonly diagnosticTestTypesDisplay = signal([
    { id: 'pre' as DiagnosticTestType, label: 'Pre' },
    { id: 'mid' as DiagnosticTestType, label: 'Mid' },
    { id: 'post' as DiagnosticTestType, label: 'Post' }
  ]);
  readonly selectedStudentIds = signal<number[]>([]);
  readonly isAllTestTypes = computed(() => this.selectedTestTypes().length === 0);
  readonly typeFilteredStudents = computed(() => {
    const types = this.selectedTestTypes();
    if (this.printType() !== 'diagnostic' || types.length === 0) return this.students();
    const typeNums = new Set<number>();
    if (types.includes('pre'))  typeNums.add(1);
    if (types.includes('mid'))  typeNums.add(3);
    if (types.includes('post')) typeNums.add(4);
    return this.students().filter(s =>
      s.assessmentScores?.some(a => !!a.dateCompleted && typeNums.has(a.assessmentType)) ?? false
    );
  });
  readonly filteredStudents = computed(() => {
    const search = this.searchTerm().toLowerCase();
    return search
      ? this.typeFilteredStudents().filter(s => s.firstName.toLowerCase().includes(search) || s.lastName.toLowerCase().includes(search))
      : this.typeFilteredStudents();
  });
  readonly printTitle = computed(() => {
    if (this.printType() === 'diagnostic') {
      return 'Batch Print Diagnostic Details';
    } else if (this.printType() === 'student-summary') {
      return 'Batch Print Student Summary';
    }

    return 'Batch Print';
  });

  constructor() {
    effect(() => {
      const dialog = this.dialogElement()?.nativeElement;
      if (!dialog) return ;

      if (this.isOpen())
      {
        dialog.showModal();
      }
      else
      {
        dialog.close();
        // Now reset our signals
        this.selectedStudentIds.set([]);
        this.selectedTestTypes.set([]);
        this.searchTerm.set('');
        this.printAll.set(true);
      }
    });
  }

  toggleStudent(studentId: number) {
    // If we are toggling a student already in the selected, remove the student id
    // If we are toggling a deselected student, add the student id
    this.selectedStudentIds.update(ids =>
      ids.includes(studentId)
        ? ids.filter(id => id !== studentId)
        : [...ids, studentId]
    );
  }

  toggleTestType(type: DiagnosticTestType) {
    this.selectedTestTypes.update(types =>
      types.includes(type)
        ? types.filter(t => t !== type)
        : [...types, type]
    );
  }

  clearTestTypes() {
    this.selectedTestTypes.set([]);
  }

  deselectAllStudents() {
    this.selectedStudentIds.set([]);
  }

  handleGenerate() {
    const studentIds = this.printAll()
      ? this.typeFilteredStudents().map(s => s.userID)
      : this.selectedStudentIds();

    if (studentIds.length > 0) {
      this.onPrint.emit({
        studentIds,
        ...(this.printType() === 'diagnostic' ? { testTypes: this.selectedTestTypes() } : {}),
      });
    }
  }

  handleCancel() {
    this.onClose.emit();
  }
}
